import { AfterViewInit, Component, ElementRef, inject, input, OnInit, output, viewChild } from "@angular/core";
import { DashboardVisibilityOptions, GlobalMilestoneWithMeta, MilestoneStatus } from "@ankaadia/graphql";
import { MilestoneTimelineRendererService } from "../milestone-timeline-renderer.service";
import { MilestoneViewService } from "../milestone-view.service";

@Component({
  selector: "new-ui-milestone-aggregated-view",
  templateUrl: "./new-ui-milestone-aggregated-view.component.html",
})
export class NewUiMilestoneAggregatedViewComponent implements OnInit, AfterViewInit {
  private readonly milestoneViewService = inject(MilestoneViewService);
  private readonly timelineRendererService = inject(MilestoneTimelineRendererService);

  protected readonly timelineContainer = viewChild<ElementRef>("timelineContainer");

  readonly readonly = input.required<boolean>();
  readonly candidateSum = output<number>();
  readonly rowCount = output<number>();

  protected milestoneData: GlobalMilestoneWithMeta[];

  resizeObserver?: ResizeObserver;

  // Sample data for testing the timeline renderer
  readonly sampleMilestones: GlobalMilestoneWithMeta[] = [
    {
      __typename: "GlobalMilestoneWithMeta",
      id: "milestone-1",
      status: MilestoneStatus.Finished,
      localizedName: "Initial Application",
      defaultDurationInDays: 7,
      avgDurationInDays: 5,
      icon: "pi pi-file",
      index: 125,
      activeTaskIds: [],
    },
    {
      __typename: "GlobalMilestoneWithMeta",
      id: "milestone-2",
      status: MilestoneStatus.Finished,
      localizedName: "Document Verification",
      defaultDurationInDays: 14,
      avgDurationInDays: 12,
      icon: "pi pi-check-square",
      index: 87,
      activeTaskIds: [],
    },
    {
      __typename: "GlobalMilestoneWithMeta",
      id: "milestone-3",
      status: MilestoneStatus.Active,
      localizedName: "Language Assessment",
      defaultDurationInDays: 21,
      avgDurationInDays: 18,
      icon: "pi pi-comments",
      index: 56,
      activeTaskIds: [
        {
          __typename: "TaskIdentifier",
          taskId: "task-101",
          processId: "process-201",
          organizationId: "org-301",
        },
      ],
    },
    {
      __typename: "GlobalMilestoneWithMeta",
      id: "milestone-4",
      endDate: null,
      status: MilestoneStatus.Pending,
      localizedName: "Interview Scheduling",
      defaultDurationInDays: 10,
      avgDurationInDays: 8,
      icon: "pi pi-calendar",
      index: 228,
      activeTaskIds: [],
    },
    {
      __typename: "GlobalMilestoneWithMeta",
      id: "milestone-5",
      endDate: null,
      status: MilestoneStatus.Pending,
      localizedName: "Contract Preparation",
      defaultDurationInDays: 7,
      avgDurationInDays: 6,
      icon: "pi pi-file-edit",
      index: 13,
      activeTaskIds: [],
    },
    {
      __typename: "GlobalMilestoneWithMeta",
      id: "milestone-6",
      endDate: null,
      status: MilestoneStatus.Pending,
      localizedName: "Visa Application",
      defaultDurationInDays: 30,
      avgDurationInDays: 28,
      icon: "pi pi-id-card",
      index: 109,
      activeTaskIds: [],
    },
    {
      __typename: "GlobalMilestoneWithMeta",
      id: "milestone-7",
      endDate: null,
      status: MilestoneStatus.Pending,
      localizedName: "Onboarding",
      defaultDurationInDays: 5,
      avgDurationInDays: 4,
      icon: "pi pi pi-users",
      index: 26,
      activeTaskIds: [],
    },
  ];

  ngOnInit(): void {
    // Fetch aggregated milestone data for the organization dashboard
    this.milestoneViewService
      .fetchAggregatedMilestoneData(DashboardVisibilityOptions.OwnOrganization)
      .subscribe((data) => {
        this.milestoneData = data.milestoneData;
        if (!this.milestoneData || this.milestoneData.length === 0) {
          this.candidateSum.emit(null);
          return;
        }

        this.candidateSum.emit(data.count);
        const container = this.timelineContainer();
        this.renderMilestones(container.nativeElement);
      });
  }

  ngAfterViewInit(): void {
    // Listen for container resize
    this.resizeObserver = new ResizeObserver(() => {
      const container = this.timelineContainer();
      if (this.milestoneData?.length > 0) this.renderMilestones(container.nativeElement);
    });
    this.resizeObserver.observe(this.timelineContainer().nativeElement);
  }

  /**
   * Renders the sample milestone data into the timeline container
   */
  private renderMilestones(container: HTMLElement): void {
    const { rows } = this.timelineRendererService.renderTimeline(container, this.milestoneData, {
      width: container.offsetWidth || 1100,
      rowHeight: 160,
      nodeWidth: 56,
      nodeHeight: 56,
      borderRadius: 12,
      showDuration: true,
      showSubtitle: false,
      // onMilestoneClick: (id) => {
      //   console.log("Milestone clicked:", id);
      //   // Add your click handler logic here
      // },
    });
    this.rowCount.emit(rows);
  }
}
